import { PayloadAction } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import { l } from '@manychat/manyui'

import {
  AiAlwaysOnAutomationsActions,
  AiAlwaysOnAutomationsSelectors,
} from 'apps/aiAutomation/store/core'
import { KnowledgeSocketEventsSchemas } from 'apps/aiAutomation/store/knowledge/schemas'
import { alert } from 'common/core'
import { mapResponse } from 'shared/api/lib/mapResponse'
import { AiBusinessKnowledgeApi } from 'shared/api/requests/aiBusinessKnowledge'
import {
  KnowledgeEntry,
  KnowledgeEntryDraft,
  KnowledgeEntryState,
  KnowledgeEntryType,
} from 'shared/api/requests/aiBusinessKnowledge/schemas'
import { createListSlice } from 'shared/lib/factory/redux/createListSlice'
import { createListOperations } from 'shared/lib/factory/redux/createListSlice/lib/createListOperations'
import { makeCreateAsyncAction } from 'shared/lib/redux'
import { getClientId } from 'utils/clientId'
import {
  accountNotificationsListener,
  anotherTabNotificationsListener,
} from 'utils/services/notificationsService'

const createAsyncAction = makeCreateAsyncAction({
  scope: 'ai',
  section: 'ai_always_on_automation',
})

const NAMESPACE = 'aiAutomationKnowledge'

interface ExtraState {
  loadedEntries: Record<string, 'loaded' | 'loading'>
}

const extraState: ExtraState = {
  loadedEntries: {},
}

const {
  actions,
  reducer,
  selectors,
  initialState: AiAutomationKnowledgeInitialState,
} = createListSlice({
  namespace: NAMESPACE,
  storePath: 'aiAutomation.knowledge',
  listKey: 'data',

  operations: createListOperations({
    getList: mapResponse(AiBusinessKnowledgeApi.retrieve, (response) => response.data),
    getNextList: mapResponse(AiBusinessKnowledgeApi.retrieve, (response) => response.data),
    getPrevList: undefined,
    createItem: mapResponse(AiBusinessKnowledgeApi.create, (response) => response.data.data),
    updateItem: mapResponse(AiBusinessKnowledgeApi.updateData, (response) => response.data.data),
    deleteItem: AiBusinessKnowledgeApi.delete,
  }),

  getItemId: (item) => item.id,

  extraState,
  reducers: {
    entryLoadingStatusChanged: (
      state,
      action: PayloadAction<{ id: string; status: 'loaded' | 'loading' }>,
    ) => {
      state.loadedEntries[action.payload.id] = action.payload.status
    },
  },
  extraReducers: (builder) => {
    builder.addCase(ensureEntryLoadedFx.fulfilled, (state, action) => {
      if (action.payload) {
        state.loadedEntries[action.payload] = 'loaded'
      }
    })

    builder.addCase(actions.createItemFx.fulfilled, (state, action) => {
      state.loadedEntries[action.payload.id] = 'loaded'
    })

    builder.addMatcher(actions.deleteItem.match, (state) => {
      if (state.listEarlyLimiter !== null) {
        state.listEarlyLimiter = state.listEarlyLimiter - 1
      }
    })

    builder.addMatcher(actions.prependItem.match, (state) => {
      if (state.listEarlyLimiter !== null) {
        state.listEarlyLimiter = state.listEarlyLimiter + 1
      }
    })
  },
})

const addEntryFx = createAsyncAction(
  `${NAMESPACE}/addEntryFx`,
  async (data: KnowledgeEntryDraft, { getState, dispatch }) => {
    const isAutomationPublished = AiAlwaysOnAutomationsSelectors.getFaqScenarioStatus(getState())

    await dispatch(
      actions.createItemFx({
        effectConfig: {
          createdItemAction: 'prepend',
        },
        payload: {
          query: { client_id: getClientId() },
          body: { data },
        },
      }),
    ).unwrap()

    if (isAutomationPublished) {
      alert(l.translate('Info saved! AI will now use this to answer questions.'), 'success', {
        icon: 'CheckCircle',
      })
    }
  },
)

const editEntryFx = createAsyncAction(
  `${NAMESPACE}/editEntryFx`,
  async (data: KnowledgeEntry, { getState, dispatch }) => {
    const isAutomationPublished = AiAlwaysOnAutomationsSelectors.getFaqScenarioStatus(getState())

    await dispatch(
      actions.updateItemFx({
        id: data.id,
        payload: {
          query: { client_id: getClientId() },
          body: { data },
        },
      }),
    ).unwrap()

    if (isAutomationPublished) {
      alert(
        l.translate(
          'Knowledge updated. AI is now using the latest version to answer customer questions.',
        ),
        'success',
        {
          icon: 'CheckCircle',
        },
      )
    }
  },
)

const removeEntryFx = createAsyncAction(
  `${NAMESPACE}/removeEntryFx`,
  async (id: string, { dispatch, getState }) => {
    const entries = selectors.getList(getState())
    const isAutomationPublished = AiAlwaysOnAutomationsSelectors.getFaqScenarioStatus(getState())

    await dispatch(
      actions.deleteItemFx({
        id,
        payload: {
          query: { client_id: getClientId() },
          body: { id },
        },
      }),
    ).unwrap()

    if (entries.length === 1) {
      await dispatch(
        AiAlwaysOnAutomationsActions.updateConfigFx({
          scenarios: {
            faq: {
              published: false,
            },
          },
        }),
      ).unwrap()
    }

    if (isAutomationPublished) {
      alert(
        entries.length === 1
          ? l.translate('All the info was deleted! AI will no longer answer questions.')
          : l.translate('Info deleted! AI won’t answer questions related to that anymore.'),
        'success',
        {
          icon: 'CheckCircle',
        },
      )
    }
  },
)

const ensureEntryLoadedFx = createAsyncAction(
  `${NAMESPACE}/ensureEntryLoadedFx`,
  async (id: string, { dispatch, getState }) => {
    const loadedEntries = AiAutomationKnowledgeSelectors.getLoadedEntries(getState())
    const isTextEntry = selectors.getItemById(getState(), id).type === KnowledgeEntryType.TEXT

    if (id === null || id in loadedEntries || !isTextEntry) {
      return
    }

    dispatch(actions.entryLoadingStatusChanged({ id, status: 'loading' }))

    const { data } = await AiBusinessKnowledgeApi.get({ query: { entry_id: id } })

    dispatch(actions.updateItem({ id, data: data.data }))

    return id
  },
)

export const AiAutomationKnowledgeReducer = reducer

export const AiAutomationKnowledgeActions = {
  getListFx: actions.getListFx,
  getNextListFx: actions.getNextListFx,

  addEntryFx,
  editEntryFx,
  removeEntryFx,
  ensureEntryLoadedFx,

  entryDeleted: actions.deleteItem,
}

anotherTabNotificationsListener.on(
  KnowledgeSocketEventsSchemas.entryCreated,
  (payload, dispatch) => {
    dispatch(actions.prependItem({ item: payload.data }))
    dispatch(actions.entryLoadingStatusChanged({ id: payload.data.id, status: 'loaded' }))
  },
)

anotherTabNotificationsListener.on(
  KnowledgeSocketEventsSchemas.entryUpdated,
  (payload, dispatch) => {
    dispatch(actions.updateItem({ id: payload.data.id, data: payload.data }))
    dispatch(actions.entryLoadingStatusChanged({ id: payload.data.id, status: 'loaded' }))
  },
)

anotherTabNotificationsListener.on(
  KnowledgeSocketEventsSchemas.entryDeleted,
  (payload, dispatch) => {
    dispatch(actions.deleteItem({ id: payload.data.id }))
  },
)

accountNotificationsListener.on(
  KnowledgeSocketEventsSchemas.entryStateUpdated,
  (payload, dispatch) => {
    dispatch(
      actions.updateItem({
        id: payload.data.id,
        data: payload.data,
      }),
    )
  },
)

const getState = (state: RootState) => state.aiAutomation.knowledge

const getProcessedEntries = createSelector([selectors.getList], (entries) =>
  entries.filter((entry) => entry.state === KnowledgeEntryState.PROCESSED),
)

const getUsedLinks = createSelector([selectors.getList], (knowledge) =>
  knowledge.reduce<string[]>((result, entry) => {
    if (entry.type === KnowledgeEntryType.LINK) {
      result.push(entry.data.url)
    }

    return result
  }, []),
)

export const AiAutomationKnowledgeSelectors = {
  ...selectors,
  getProcessedEntries,
  getUsedLinks,
  getIsNextPageAvailable: (state: RootState) => Boolean(getState(state).limiter),
  getIsItemLoading: (state: RootState, id: string | null) => {
    if (id === null) {
      return selectors.getIsNewItemCreating(state)
    }

    const loadedEntries = getState(state).loadedEntries

    return selectors.getIsItemProcessingAction(state, id) || loadedEntries[id] === 'loading'
  },
  getIsAllEntriesInvalid: createSelector(
    [selectors.getList],
    (entries) =>
      entries.length > 0 && entries.every((entry) => entry.state === KnowledgeEntryState.FAILED),
  ),
  getIsItemLoaded: (state: RootState, id: string | null) => {
    if (id === null) {
      return false
    }

    return getState(state).loadedEntries[id] === 'loaded'
  },
  getLoadedEntries: (state: RootState) => getState(state).loadedEntries,
  getEntriesByType: createSelector(
    [selectors.getList, (_, type: KnowledgeEntryType) => type],
    (list, type) => list.filter((entry) => entry.type === type),
  ),
}

export { AiAutomationKnowledgeInitialState }
export type AiAutomationKnowledgeState = typeof AiAutomationKnowledgeInitialState
